import React from 'react';
import { IData, IHeader } from '../types';
import * as Styles from './styles';
import { useViewport } from 'utils/responsive';

interface IProps {
    activeFilter: string;
    headers: IHeader[];
    data: IData[];
    totalPages: number;
    onNext: () => void;
    onBack: () => void;
    currentPage: number;
    totalResults?: number;
}

const PaginatedTable: React.FC<IProps> = (props) => {
    const {
        activeFilter,
        headers,
        data,
        totalPages,
        onNext,
        onBack,
        currentPage,
        totalResults,
    } = props;

    const { isMobile } = useViewport();

    const formatCurrency = (value: string | number) => {
        if (typeof value === 'string') {
            if (value.includes('₹')) return value;
            const num = parseFloat(value);
            if (!Number.isNaN(num)) {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                }).format(num);
            }
            return value;
        }
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(value);
    };

    const formatDate = (value: string | number) => {
        if (typeof value === 'string') {
            // If it's already in YYYY-MM-DD format, return as is
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            // Try to parse and format
            const date = new Date(value);
            if (!Number.isNaN(date.getTime())) {
                return date.toISOString().split('T')[0];
            }
            return value;
        }
        return value;
    };

    const isCommissionColumn = (headerKey: string) => {
        return (
            headerKey.toLowerCase().includes('commission') ||
            headerKey.toLowerCase().includes('earned')
        );
    };

    const isDateColumn = (headerKey: string) => {
        return headerKey.toLowerCase().includes('date');
    };

    const isCurrencyColumn = (headerKey: string) => {
        return (
            headerKey.toLowerCase().includes('cost') ||
            headerKey.toLowerCase().includes('amount') ||
            headerKey.toLowerCase().includes('price')
        );
    };

    return (
        <>
            {data.length === 0 && activeFilter !== 'TRANSACTION' ? (
                <Styles.NoResult>{'No commissions'}</Styles.NoResult>
            ) : (
                <Styles.TableCard>
                    <Styles.TableHead>
                        <Styles.TableTitle>
                            {'Commission Results by '}
                            <Styles.FilterText>
                                {activeFilter.toLowerCase()}
                            </Styles.FilterText>
                        </Styles.TableTitle>
                        {totalResults && (
                            <Styles.TotalResults>
                                {totalResults} {'total results'}
                            </Styles.TotalResults>
                        )}
                    </Styles.TableHead>
                    <Styles.TableContainer>
                        <Styles.StyledTable>
                            <thead>
                                <tr>
                                    {headers?.map((header) => (
                                        <Styles.TableHeader key={header.key}>
                                            {header.name}
                                        </Styles.TableHeader>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data?.map((item, index) => (
                                    <Styles.TableRow key={index}>
                                        {headers?.map((header) => {
                                            const value =
                                                item[header.key as keyof IData];
                                            let displayValue = value;

                                            if (isDateColumn(header.key)) {
                                                displayValue =
                                                    formatDate(value);
                                            } else if (
                                                isCurrencyColumn(header.key) ||
                                                isCommissionColumn(header.key)
                                            ) {
                                                displayValue =
                                                    formatCurrency(value);
                                            }

                                            return (
                                                <Styles.TableData
                                                    key={header.key}
                                                >
                                                    {isCommissionColumn(
                                                        header.key
                                                    ) ? (
                                                        <Styles.CommissionValue>
                                                            {displayValue}
                                                        </Styles.CommissionValue>
                                                    ) : (
                                                        displayValue
                                                    )}
                                                </Styles.TableData>
                                            );
                                        })}
                                    </Styles.TableRow>
                                ))}
                            </tbody>
                        </Styles.StyledTable>
                    </Styles.TableContainer>
                    {isMobile ? (
                        <Styles.PaginationContainer>
                            <Styles.PaginationButtons>
                                <Styles.PageButton
                                    onClick={onBack}
                                    disabled={currentPage === 1}
                                    isActive={currentPage > 1}
                                >
                                    &lt; {'Previous'}
                                </Styles.PageButton>
                                <Styles.PaginationInfo>
                                    Page {currentPage} of {totalPages}
                                    <br />({data.length} of{' '}
                                    {totalResults || 'unknown'} results)
                                </Styles.PaginationInfo>
                                <Styles.PageButton
                                    onClick={onNext}
                                    disabled={currentPage === totalPages}
                                    isActive={currentPage < totalPages}
                                >
                                    {'Next'} &gt;
                                </Styles.PageButton>
                            </Styles.PaginationButtons>
                        </Styles.PaginationContainer>
                    ) : (
                        <Styles.PaginationContainer>
                            <Styles.PaginationInfo>
                                Page {currentPage} of {totalPages} (showing{' '}
                                {data.length} of {totalResults || 'unknown'}{' '}
                                results)
                            </Styles.PaginationInfo>
                            <Styles.PaginationButtons>
                                <Styles.PageButton
                                    onClick={onBack}
                                    disabled={currentPage === 1}
                                    isActive={currentPage > 1}
                                >
                                    &lt; {'Previous'}
                                </Styles.PageButton>
                                <Styles.PageButton
                                    onClick={onNext}
                                    disabled={currentPage === totalPages}
                                    isActive={currentPage < totalPages}
                                >
                                    {'Next'} &gt;
                                </Styles.PageButton>
                            </Styles.PaginationButtons>
                        </Styles.PaginationContainer>
                    )}
                </Styles.TableCard>
            )}
        </>
    );
};

export default PaginatedTable;
